/**
 * URL trust policy: keep one-time auth tokens (password reset / invite / login-by-token /
 * email verify) out of URLs that reach the address bar, logs, and third parties, and
 * validate URLs that arrive from outside the app (push-notification payloads, signed
 * upload URLs) before they are navigated to or uploaded to.
 */

/** Query params that may carry one-time auth secrets — scrubbed before logging. */
export const SENSITIVE_QUERY_KEYS = ['token', 'reset_token', 'invite_token', 'auth_token']

/** Drop sensitive query params (auth tokens) from a URL before it reaches logs/Sentry. */
export function sanitizeLoggedUrl(raw: string): string {
  try {
    const u = new URL(raw, 'http://placeholder.local')
    let changed = false
    for (const key of SENSITIVE_QUERY_KEYS) {
      if (u.searchParams.has(key)) {
        u.searchParams.delete(key)
        changed = true
      }
    }
    if (!changed) {
      return raw
    }
    const next = u.pathname + u.search + u.hash
    return u.origin === 'http://placeholder.local' ? next : new URL(next, u.origin).href
  } catch {
    return raw
  }
}

/**
 * Remove sensitive one-time params (auth tokens) from the address bar URL without
 * triggering a vue-router navigation. Using `history.replaceState` (instead of
 * `router.replace`) avoids re-running route guards — after the token is stripped,
 * `guestGuard` would no longer treat the path as a token-based guest route and could
 * bounce an authenticated user off the page.
 *
 * The token is still captured in the calling component's ref, so the page flow
 * (reset / invite / verify / login-by-token) continues normally after the strip.
 */
export function stripSensitiveQueryParams(keys: string[] = SENSITIVE_QUERY_KEYS): void {
  if (typeof window === 'undefined') {
    return
  }
  const url = new URL(window.location.href)
  let changed = false
  for (const key of keys) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key)
      changed = true
    }
  }
  if (changed) {
    window.history.replaceState(
      window.history.state ?? {},
      '',
      url.pathname + url.search + url.hash,
    )
  }
}

/**
 * Resolve an app-internal navigation target from a URL that arrived from outside the app
 * (push notification payload, e-mail link). Relative paths resolve against `origin`;
 * anything that does not end up on `origin` returns `null` so callers skip the navigation
 * instead of redirecting the user to an attacker-controlled site.
 *
 * `origin` is passed explicitly rather than read from `window` so the same helper works in
 * the service worker, where only `self.location.origin` is available.
 */
export function toTrustedAppUrl(raw: string, origin: string): string | null {
  try {
    const base = new URL(origin)
    const target = new URL(raw, base)
    return target.origin === base.origin ? target.href : null
  } catch {
    return null
  }
}

/** Object-storage hosts that direct browser uploads may target (Azure Blob, sovereign clouds). */
const AZURE_BLOB_HOST_SUFFIXES = [
  '.blob.core.windows.net',
  '.blob.core.chinacloudapi.cn',
  '.blob.core.cloudapi.de',
  '.blob.core.usgovcloudapi.net',
]

/** Hosts that must never be reached from the browser: loopback, RFC1918, link-local, cloud metadata. */
const PRIVATE_HOST_PATTERNS = [
  /^localhost$/,
  /\.localhost$/,
  /^127\./,
  /^0\./,
  /^10\./,
  /^192\.168\./,
  /^169\.254\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
]

/** Additional upload hosts from `VITE_UPLOAD_ALLOWED_HOSTS` (comma-separated) for self-hosted storage. */
function configuredUploadHosts(): string[] {
  const configured = import.meta.env.VITE_UPLOAD_ALLOWED_HOSTS
  if (!configured) {
    return []
  }
  return configured
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean)
}

/**
 * True when a backend-supplied signed upload URL points at a known object-storage host over
 * HTTPS. Guards the direct-to-storage upload path: a spoofed or compromised
 * `prepare-file-upload` response must not make the browser PUT tenant files to an
 * attacker-controlled — or internal/metadata — host.
 */
export function isTrustedUploadUrl(raw: string): boolean {
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return false
  }
  if (url.protocol !== 'https:' || url.username || url.password) {
    return false
  }
  const host = url.hostname.toLowerCase()
  // IPv6 literals arrive bracketed, e.g. `[::1]`; none of them is a valid upload host.
  if (host.startsWith('[') || PRIVATE_HOST_PATTERNS.some((pattern) => pattern.test(host))) {
    return false
  }
  if (configuredUploadHosts().includes(host)) {
    return true
  }
  return AZURE_BLOB_HOST_SUFFIXES.some(
    (suffix) => host.length > suffix.length && host.endsWith(suffix),
  )
}
